import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Bell,
  BellOff,
  Send,
  Loader2,
  Check,
  X,
  Smartphone,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [permissionStatus, setPermissionStatus] = useState('default');

  // Check notification status on mount
  useEffect(() => {
    checkNotificationStatus();
    if ('Notification' in window) {
      setPermissionStatus(Notification.permission);
    }
  }, []);

  const checkNotificationStatus = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/push/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setNotificationsEnabled(data.enabled);
      }
    } catch (error) {
      console.error('Error checking status:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleEnableNotifications = async () => {
    console.log('[Settings] Enable notifications clicked');
    
    if (!('Notification' in window)) {
      toast.error('Notifications not supported in this browser');
      return;
    }

    // Check if already denied
    if (Notification.permission === 'denied') {
      toast.error('Notifications are blocked by your browser', {
        duration: 5000,
        description: 'Click the lock icon in your address bar to change settings.'
      });
      return;
    }

    setNotificationsLoading(true);
    try {
      console.log('[Settings] Requesting permission...');
      // Request browser permission
      const permission = await Notification.requestPermission();
      console.log('[Settings] Permission result:', permission);
      setPermissionStatus(permission);
      
      if (permission === 'granted') {
        // Get session token
        const token = localStorage.getItem('session_token');
        
        // Register with backend
        const response = await fetch(`${API_URL}/api/push/register-token`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            token: 'web-push-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9),
            device_type: 'web'
          })
        });

        if (response.ok) {
          setNotificationsEnabled(true);
          toast.success('Push notifications enabled!');
          
          // Show a test notification
          new Notification('BodyCorp365', {
            body: 'Push notifications are now enabled!',
            icon: '/logo192.png'
          });
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Failed to register');
        }
      } else if (permission === 'denied') {
        toast.error('Notifications blocked. Please enable in browser settings.', {
          duration: 5000
        });
      } else {
        toast.error('Notification permission not granted');
      }
    } catch (error) {
      console.error('[Settings] Error:', error);
      toast.error('Failed to enable notifications');
    } finally {
      setNotificationsLoading(false);
    }
  };

  const handleDisableNotifications = async () => {
    setNotificationsLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/push/unregister-token`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        setNotificationsEnabled(false);
        toast.success('Push notifications disabled');
      }
    } catch (error) {
      toast.error('Failed to disable notifications');
    } finally {
      setNotificationsLoading(false);
    }
  };

  const handleTestNotification = async () => {
    // First try local notification
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Test from BodyCorp365', {
        body: 'This is a test notification!',
        icon: '/logo192.png'
      });
      toast.success('Test notification sent!');
    } else {
      toast.error('Please enable notifications first');
    }
  };

  const getBackPath = () => {
    if (user?.role === 'trustee' || user?.role === 'property_manager') return '/admin';
    if (user?.role === 'managing_agent') return '/agent';
    if (user?.role === 'platform_owner') return '/platform';
    return '/dashboard';
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200 px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(getBackPath())}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-semibold text-slate-900">Settings</h1>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Push Notifications Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Push Notifications
            </CardTitle>
            <CardDescription>
              Receive instant alerts for maintenance updates, levy reminders, and important messages.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Status */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                {notificationsEnabled ? (
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Check className="w-5 h-5 text-green-600" />
                  </div>
                ) : (
                  <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                    <BellOff className="w-5 h-5 text-slate-500" />
                  </div>
                )}
                <div>
                  <p className="font-medium text-slate-900">
                    {checkingStatus ? 'Checking...' : notificationsEnabled ? 'Enabled' : 'Disabled'}
                  </p>
                  <p className="text-sm text-slate-500">
                    Browser permission: {permissionStatus}
                  </p>
                </div>
              </div>
            </div>

            {/* Enable/Disable Button */}
            {notificationsEnabled ? (
              <Button
                variant="outline"
                className="w-full"
                onClick={handleDisableNotifications}
                disabled={notificationsLoading}
              >
                {notificationsLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <BellOff className="w-4 h-4 mr-2" />
                )}
                Disable Notifications
              </Button>
            ) : (
              <Button
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                onClick={handleEnableNotifications}
                disabled={notificationsLoading || permissionStatus === 'denied'}
              >
                {notificationsLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Bell className="w-4 h-4 mr-2" />
                )}
                Enable Notifications
              </Button>
            )}

            {/* Permission Denied Warning */}
            {permissionStatus === 'denied' && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Notifications are blocked.</strong> To enable them:
                </p>
                <ol className="text-sm text-amber-700 mt-2 list-decimal list-inside space-y-1">
                  <li>Click the lock/info icon in your browser's address bar</li>
                  <li>Find "Notifications" and change to "Allow"</li>
                  <li>Refresh this page</li>
                </ol>
              </div>
            )}

            {/* Test Button */}
            {notificationsEnabled && (
              <Button
                variant="outline"
                className="w-full"
                onClick={handleTestNotification}
              >
                <Send className="w-4 h-4 mr-2" />
                Send Test Notification
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Smartphone className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <p className="text-sm text-slate-600">
                  Push notifications will alert you about:
                </p>
                <ul className="text-sm text-slate-500 mt-2 space-y-1">
                  <li>• New maintenance tickets</li>
                  <li>• Levy payment reminders</li>
                  <li>• Insurance claim updates</li>
                  <li>• New votes and AGM notices</li>
                  <li>• Messages from trustees</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
